import { Injectable, Type, inject } from '@angular/core';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { take } from 'rxjs';

/**
 * Implemented by a dialog's content component when leaving it can lose work.
 *
 * <p>{@link AppDialogService.openGuarded} asks before it dismisses, so a stray click on the
 * backdrop cannot silently discard something the user typed.
 */
export interface DialogDismissGuard {
  /** False keeps the dialog open. Asked only for the backdrop and Escape, never for a button. */
  canDismiss(): boolean | Promise<boolean>;

  /**
   * What to close with once dismissal is allowed. Without it a dismissed dialog closes with
   * `undefined`, which callers reading `onClose` will take to mean "nothing happened".
   */
  dismissResult?(): unknown;
}

/** How a dialog can be left, besides its own buttons. */
type Dismissal = 'free' | 'blocked' | 'guarded';

/**
 * The one place dialogs are opened.
 *
 * <p>PrimeNG's `DialogService` still does the work. This fixes the two things every call site
 * was getting wrong, neither of which is visible from a call site.
 *
 * <p><b>`dismissableMask` alone does nothing.</b> PrimeNG guards both dismissal routes with
 * `closable`, which {@code DynamicDialog} reads straight off the config through a
 * `booleanAttribute` transform — so the 21 call sites that never mentioned it all coerced to
 * `false`, and no dialog in this app could be closed by its backdrop <em>or</em> by Escape,
 * whatever `dismissableMask` and `closeOnEscape` said.
 *
 * <p><b>PrimeNG's dismissal never reaches `onClose`.</b> Its hide path calls
 * `DynamicDialogRef.destroy()`, not `close()`. Turning the built-in flags on would therefore
 * have left every `onClose` handler in the app silently dead for the dismissal case — the
 * presence row that refreshes itself, the list pages that reload, and the two `firstValueFrom`
 * awaits in `AlertService`, which would never resolve at all.
 *
 * <p>So dismissal is not delegated: the built-in flags stay off and this listens for the
 * backdrop and Escape itself, closing through `ref.close(...)` every time. One mechanism, and
 * `onClose` means the same thing however the dialog was left.
 *
 * <p>Three ways out, and a call site says which one it means:
 *
 * <ul>
 *   <li>{@link open} — a dialog you can leave. Backdrop and Escape both close it.
 *   <li>{@link openBlocking} — a dialog that must be answered. Neither does.
 *   <li>{@link openGuarded} — a dialog holding unsaved work. Both ask it first.
 * </ul>
 */
@Injectable({ providedIn: 'root' })
export class AppDialogService {
  private readonly dialogService = inject(DialogService);

  /** Tells one open dialog's backdrop from another's when they are stacked. */
  private seq = 0;

  /** A dialog the user can simply leave. */
  open<T>(component: Type<T>, config: DynamicDialogConfig = {}): DynamicDialogRef {
    return this.openWith(component, config, 'free');
  }

  /** A dialog that must be answered — a camera, a required choice, an access denial. */
  openBlocking<T>(component: Type<T>, config: DynamicDialogConfig = {}): DynamicDialogRef {
    return this.openWith(component, config, 'blocked');
  }

  /**
   * A dialog that asks its content component before the backdrop or Escape closes it.
   *
   * <p>The content component must implement {@link DialogDismissGuard}. One that does not
   * cannot be dismissed at all — this fails closed on purpose. A guarded dialog is guarded
   * because leaving it loses work, so a missing interface has to cost a dialog that only its
   * own buttons will close, never a silently discarded edit.
   *
   * <p>That also makes this the migration path for the form dialogs: each one is safe here
   * from the day it is routed through this method, and becomes dismissible the day it says
   * what dismissing it should do.
   */
  openGuarded<T>(component: Type<T>, config: DynamicDialogConfig = {}): DynamicDialogRef {
    return this.openWith(component, config, 'guarded');
  }

  private openWith<T>(component: Type<T>, config: DynamicDialogConfig, dismissal: Dismissal): DynamicDialogRef {
    const marker = `sm-dialog-${++this.seq}`;
    const ref = this.dialogService.open(component, {
      showHeader: false,
      ...config,
      // The backdrop is found by this class rather than by reaching into the dialog instance:
      // `maskStyleClass` lands on the very element PrimeNG's own listener tests against, so
      // this watches exactly what that would have watched.
      maskStyleClass: [config.maskStyleClass, marker].filter(Boolean).join(' '),
      // Off deliberately — see the class comment. Dismissal is handled below so that it
      // closes rather than destroys, and so that a guard gets a say.
      dismissableMask: false,
      closeOnEscape: false,
    })!;

    if (dismissal !== 'blocked') {
      ref.onChildComponentLoaded
        .pipe(take(1))
        .subscribe((instance) =>
          this.listenForDismissal(ref, marker, dismissal, instance as Partial<DialogDismissGuard>),
        );
    }

    return ref;
  }

  /** Backdrop and Escape, re-implemented on top of {@link DialogDismissGuard}. */
  private listenForDismissal(
    ref: DynamicDialogRef,
    marker: string,
    dismissal: Dismissal,
    instance: Partial<DialogDismissGuard>,
  ): void {
    const declaresGuard = typeof instance?.canDismiss === 'function';
    // Fails closed: a guarded dialog whose content component declares no guard keeps its work.
    if (dismissal === 'guarded' && !declaresGuard) return;
    const mayDismiss = declaresGuard ? () => instance.canDismiss!() : () => true;

    // One question at a time: the guard is async, and its own confirmation dialog is a click
    // and an Escape away from asking again on top of itself.
    let asking = false;

    const dismiss = async (): Promise<void> => {
      if (asking) return;
      asking = true;
      try {
        if (await mayDismiss()) ref.close(instance?.dismissResult?.());
      } finally {
        asking = false;
      }
    };

    const onMouseDown = (event: MouseEvent): void => {
      if (event.target instanceof Element && event.target.classList.contains(marker)) void dismiss();
    };

    // Only when nothing is stacked on top: a guard that opens a confirmation must not answer
    // the Escape aimed at that confirmation.
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && this.isTopmost(marker)) void dismiss();
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    ref.onDestroy.pipe(take(1)).subscribe(() => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    });
  }

  /** Whether the marked backdrop is the last one opened, and so the one Escape belongs to. */
  private isTopmost(marker: string): boolean {
    const masks = document.querySelectorAll('.p-dialog-mask');
    return masks.length > 0 && masks[masks.length - 1].classList.contains(marker);
  }
}
